import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../services/api.service';
import { DatabaseLog, Label, Client } from '../../models/database-log.model';
import { Version } from '../../models/version.model';
import { WorkstationRoleType, RoleArtifactStatus } from '../../models/client-management.model';

@Component({
  selector: 'app-db-changes',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './db-changes.component.html',
  styleUrls: ['./db-changes.component.scss']
})
export class DbChangesComponent implements OnInit {
  @ViewChild('exportAnchor') exportAnchor!: ElementRef<HTMLAnchorElement>;

  currentUser: string = '';
  isAuthenticated: boolean = false;
  databases: string[] = [];
  selectedDatabase: string = '';
  versions: Version[] = [];
  selectedVersion: Version | null = null;
  logs: DatabaseLog[] = [];
  filteredLogs: DatabaseLog[] = [];

  // Labels
  availableLabels: Label[] = [];
  selectedLogForLabels: DatabaseLog | null = null;
  showLabelModal: boolean = false;

  // Clients
  clients: Client[] = [];
  selectedClient: Client | null = null;

  // Filters
  objectTypeFilter: string = '';
  userNameFilter: string = '';
  dateFromFilter: string = '';
  dateToFilter: string = '';
  objectNameFilter: string = '';

  // Label filter
  selectedLabelFilters: Label[] = [];
  labelSearchText: string = '';
  filteredLabelOptions: Label[] = [];
  showLabelSuggestions: boolean = false;
  labelMatchMode: 'any' | 'all' = 'any';

  // New log form
  showNewLogForm: boolean = false;
  newLog: Partial<DatabaseLog> = {};

  // New version form
  showNewVersionForm: boolean = false;
  newVersionName: string = '';
  previousVersionName: string = '';
  roleTypes: WorkstationRoleType[] = [];
  selectedRoleTypeIds: number[] = [];
  roleArtifactStatuses: RoleArtifactStatus[] = [];

  // Pagination
  currentPage: number = 1;
  pageSize: number = 50;
  pageSizeOptions: number[] = [50, 100, 1000];
  totalCount: number = 0;
  totalPages: number = 0;

  loading: boolean = false;
  error: string = '';

  // Expose Math for template
  Math = Math;

  constructor(private apiService: ApiService) { }

  ngOnInit(): void {
    this.loadCurrentUser();
  }

  loadCurrentUser(): void {
    this.apiService.getCurrentUser().subscribe({
      next: (user) => {
        this.currentUser = user.username;
        this.isAuthenticated = user.isAuthenticated;
        console.log('Current user:', user);
        this.loadDatabases();
      },
      error: (err) => {
        console.error('Authentication error:', err);
        this.error = 'Authentication failed. Windows Authentication may not be configured properly.';
        // Try to load databases anyway for development
        this.loadDatabases();
      }
    });
  }

  loadDatabases(): void {
    this.loading = true;
    this.error = '';
    this.apiService.getTrackedDatabases().subscribe({
      next: (databases) => {
        this.databases = databases;
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Failed to load databases: ' + err.message;
        this.loading = false;
      }
    });
  }

  onDatabaseChange(): void {
    if (this.selectedDatabase) {
      this.loadVersions();
      this.loadLabels();
      this.loadClients();
    }
  }

  loadVersions(): void {
    this.loading = true;
    this.error = '';
    this.apiService.getVersions(this.selectedDatabase).subscribe({
      next: (versions) => {
        this.versions = versions;
        if (versions.length > 0) {
          this.selectedVersion = versions[0]; // Select latest version
          this.loadLogs();
        } else {
          this.error = 'No versions found. The database may not be set up yet. Please run Setup_Database.sql script.';
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading versions:', err);
        if (err.error && err.error.error && (err.error.error.includes('Invalid object name') || err.error.error.includes('Invalid column name'))) {
          this.error = '⚠️ Database schema is outdated! Navigate to Databases page and click "Track Database" button for  "' + this.selectedDatabase + '" database. ';
        } else {
          this.error = 'Failed to load versions: ' + (err.error?.message || err.message || 'Unknown error');
        }
        this.loading = false;
      }
    });
  }

  onVersionChange(): void {
    if (this.selectedVersion) {
      this.loadLogs();
    }
  }

  loadLogs(): void {
    if (!this.selectedVersion) return;

    this.loading = true;
    this.error = '';

    const labelIds = this.selectedLabelFilters.map(l => l.labelID);

    this.apiService.getLogsFromVersion(
      this.selectedDatabase,
      this.selectedVersion.versionID,
      this.objectTypeFilter || undefined,
      this.userNameFilter || undefined,
      this.currentPage,
      this.pageSize,
      this.dateFromFilter || undefined,
      this.dateToFilter || undefined,
      this.objectNameFilter || undefined,
      labelIds.length > 0 ? labelIds : undefined,
      this.labelMatchMode
    ).subscribe({
      next: (response) => {
        this.logs = response.data;
        this.totalCount = response.totalCount;
        this.totalPages = response.totalPages;
        this.currentPage = response.page;
        this.applyFilters();
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Failed to load logs: ' + err.message;
        this.loading = false;
      }
    });
  }

  applyFilters(): void {
    // Filters are now applied on backend, just filter deleted on frontend
    this.filteredLogs = this.logs.filter(log => {
      const notDeleted = !log.commandText?.startsWith('-- DELETED --');
      return notDeleted;
    });
  }

  // Label filter autocomplete methods
  onLabelSearchChange(): void {
    if (!this.labelSearchText.trim()) {
      this.filteredLabelOptions = [];
      this.showLabelSuggestions = false;
      return;
    }

    const searchLower = this.labelSearchText.toLowerCase();
    const selectedIds = this.selectedLabelFilters.map(l => l.labelID);

    this.filteredLabelOptions = this.availableLabels.filter(label =>
      label.labelDesc.toLowerCase().includes(searchLower) &&
      !selectedIds.includes(label.labelID)
    );
    this.showLabelSuggestions = this.filteredLabelOptions.length > 0;
  }

  selectLabelFilter(label: Label): void {
    if (!this.selectedLabelFilters.find(l => l.labelID === label.labelID)) {
      this.selectedLabelFilters.push(label);
      this.labelSearchText = '';
      this.filteredLabelOptions = [];
      this.showLabelSuggestions = false;
      this.currentPage = 1;
      this.loadLogs();
    }
  }

  removeLabelFilter(label: Label): void {
    this.selectedLabelFilters = this.selectedLabelFilters.filter(l => l.labelID !== label.labelID);
    this.currentPage = 1;
    this.loadLogs();
  }

  onLabelMatchModeChange(): void {
    if (this.selectedLabelFilters.length > 0) {
      this.currentPage = 1;
      this.loadLogs();
    }
  }

  hideLabelSuggestions(): void {
    // Delay to allow click on suggestion
    setTimeout(() => {
      this.showLabelSuggestions = false;
    }, 200);
  }

  clearAllFilters(): void {
    this.objectTypeFilter = '';
    this.userNameFilter = '';
    this.dateFromFilter = '';
    this.dateToFilter = '';
    this.objectNameFilter = '';
    this.selectedLabelFilters = [];
    this.labelSearchText = '';
    this.labelMatchMode = 'any';
    this.currentPage = 1;
    this.loadLogs();
  }

  getUniqueObjectTypes(): string[] {
    return [...new Set(this.logs.map(log => log.objectType).filter(type => type))].sort() as string[];
  }

  getUniqueUserNames(): string[] {
    return [...new Set(this.logs.map(log => log.userName).filter(name => name))].sort() as string[];
  }

  // Pagination methods
  onPageSizeChange(): void {
    this.currentPage = 1; // Reset to first page when changing page size
    this.loadLogs();
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadLogs();
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);

    // Adjust start if we're near the end
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  deleteLog(log: DatabaseLog): void {
    if (confirm(`Are you sure you want to mark log ${log.id} as deleted?`)) {
      this.apiService.markAsDeleted(this.selectedDatabase, log.id).subscribe({
        next: () => {
          this.loadLogs();
        },
        error: (err) => {
          this.error = 'Failed to delete log: ' + err.message;
        }
      });
    }
  }

  openNewLogForm(): void {
    this.showNewLogForm = true;
    this.newLog = {
      schemaName: 'dbo',
      eventType: 'MANUAL_ENTRY'
    };
  }

  cancelNewLog(): void {
    this.showNewLogForm = false;
    this.newLog = {};
  }

  saveNewLog(): void {
    this.loading = true;
    this.apiService.createLog(this.selectedDatabase, this.newLog).subscribe({
      next: () => {
        this.showNewLogForm = false;
        this.newLog = {};
        this.loadLogs();
      },
      error: (err) => {
        this.error = 'Failed to create log: ' + err.message;
        this.loading = false;
      }
    });
  }

  openNewVersionForm(): void {
    this.showNewVersionForm = true;
    this.newVersionName = '';
    this.selectedRoleTypeIds = [];
    this.previousVersionName = this.versions.find(v => v.versionName !== 'Unreleased')?.versionName ?? '';
    this.loadRoleTypes();
  }

  cancelNewVersion(): void {
    this.showNewVersionForm = false;
    this.newVersionName = '';
    this.previousVersionName = '';
    this.selectedRoleTypeIds = [];
  }

  saveNewVersion(): void {
    if (!this.newVersionName) {
      alert('Please enter a version name');
      return;
    }

    if (this.newVersionName.trim().toLowerCase() === 'unreleased') {
      alert("'Unreleased' is a reserved version name and cannot be used.");
      return;
    }

    this.loading = true;
    const versionToCreate = this.newVersionName;
    const rolesWithArtifacts = this.selectedRoleTypeIds.filter(id => this.artifactStatus(id));
    this.apiService.createVersion(this.selectedDatabase, versionToCreate, undefined, this.selectedRoleTypeIds).subscribe({
      next: () => {
        this.showNewVersionForm = false;
        this.newVersionName = '';
        this.selectedRoleTypeIds = [];
        this.loadVersions();
        rolesWithArtifacts.forEach(id => {
          this.apiService.uploadRoleArtifact(id, versionToCreate).subscribe({ error: () => {} });
        });
      },
      error: (err) => {
        this.error = 'Failed to create version: ' + err.message;
        this.loading = false;
      }
    });
  }

  loadRoleTypes(): void {
    this.apiService.getRoleTypes(this.selectedDatabase || undefined).subscribe({
      next: (roles) => {
        this.roleTypes = roles;
      },
      error: (err) => {
        console.error('Failed to load role types:', err);
      }
    });
    this.apiService.getRoleArtifactStatuses().subscribe({
      next: (statuses) => { this.roleArtifactStatuses = statuses; },
      error: () => {}
    });
  }

  artifactStatus(roleTypeId: number): RoleArtifactStatus | undefined {
    return this.roleArtifactStatuses.find(s => s.roleTypeID === roleTypeId);
  }

  toggleRoleType(roleTypeId: number): void {
    const index = this.selectedRoleTypeIds.indexOf(roleTypeId);
    if (index === -1) {
      this.selectedRoleTypeIds.push(roleTypeId);
    } else {
      this.selectedRoleTypeIds.splice(index, 1);
    }
  }

  isRoleTypeSelected(roleTypeId: number): boolean {
    return this.selectedRoleTypeIds.includes(roleTypeId);
  }

  exportVersion(): void {
    if (!this.selectedVersion) return;

    this.loading = true;
    const clientId = this.selectedClient?.clientParamID;
    this.apiService.exportVersion(this.selectedDatabase, this.selectedVersion.versionID, clientId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const clientSuffix = this.selectedClient ? `_${this.selectedClient.clientName}` : '';
        const rawName = `${this.selectedDatabase}_${this.selectedVersion?.versionName}${clientSuffix}_${new Date().toISOString().split('T')[0]}.sql`;
        const safeName = rawName.replace(/[^a-zA-Z0-9._\-\s]/g, '_');
        const anchor = this.exportAnchor.nativeElement;
        anchor.href = url;
        anchor.download = safeName;
        anchor.click();
        setTimeout(() => window.URL.revokeObjectURL(url), 0);
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Failed to export version: ' + err.message;
        this.loading = false;
      }
    });
  }

  // Label management methods
  loadLabels(): void {
    this.apiService.getLabels(this.selectedDatabase).subscribe({
      next: (labels) => {
        this.availableLabels = labels;
      },
      error: (err) => {
        console.error('Failed to load labels:', err);
      }
    });
  }

  openLabelModal(log: DatabaseLog): void {
    this.selectedLogForLabels = log;
    this.showLabelModal = true;
  }

  closeLabelModal(): void {
    this.showLabelModal = false;
    this.selectedLogForLabels = null;
  }

  toggleLabel(label: Label): void {
    if (!this.selectedLogForLabels) return;

    const logLabels = this.selectedLogForLabels.labels || [];
    const hasLabel = logLabels.includes(label.labelDesc);

    if (hasLabel) {
      // Remove label
      this.apiService.removeLabel(this.selectedDatabase, this.selectedLogForLabels.id, label.labelID).subscribe({
        next: () => {
          this.selectedLogForLabels!.labels = logLabels.filter(l => l !== label.labelDesc);
          this.loadLogs(); // Refresh to get updated labels
        },
        error: (err) => {
          this.error = 'Failed to remove label: ' + err.message;
        }
      });
    } else {
      // Add label
      this.apiService.assignLabel(this.selectedDatabase, this.selectedLogForLabels.id, label.labelID).subscribe({
        next: () => {
          if (!this.selectedLogForLabels!.labels) {
            this.selectedLogForLabels!.labels = [];
          }
          this.selectedLogForLabels!.labels.push(label.labelDesc);
          this.loadLogs(); // Refresh to get updated labels
        },
        error: (err) => {
          this.error = 'Failed to assign label: ' + err.message;
        }
      });
    }
  }

  hasLabel(label: Label): boolean {
    if (!this.selectedLogForLabels) return false;
    const logLabels = this.selectedLogForLabels.labels || [];
    return logLabels.includes(label.labelDesc);
  }

  // Client management methods
  loadClients(): void {
    this.apiService.getClients().subscribe({
      next: (clients) => {
        this.clients = clients;
      },
      error: (err) => {
        console.error('Failed to load clients:', err);
      }
    });
  }

  onClientChange(): void {
    // Client selection changed - could trigger UI updates
    console.log('Selected client:', this.selectedClient);
  }
}

